import _ from 'lodash'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'
import { ObjectShape } from 'yup/lib/object'
import auth from '../../../api/auth/hooks'
import { LocationSource } from '../../../api/globalTypes'
import { Property, PropertyCreatePayload } from '../../../api/properties/types'
import { APPLICATION_SETTINGS, REAL_ESTATE_SERVICE, REAL_ESTATE_TYPES } from '../../../common/constants'
import { realEstateGeneralFields } from '../../../common/forms/generalRealEstate/fields'
import {
    realEstateGeneralFlatLeaseInputs,
    realEstateGeneralFlatSaleInputs,
    realEstateGeneralGarageLeaseInputs,
    realEstateGeneralGarageSaleInputs,
    realEstateGeneralHouseLeaseInputs,
    realEstateGeneralHouseSaleInputs,
    realEstateGeneralInputs,
    realEstateGeneralLandLeaseInputs,
    realEstateGeneralLandSaleInputs,
    realEstateGeneralOfficeSpaceLeaseInputs,
    realEstateGeneralOfficeSpaceSaleInputs,
} from '../../../common/forms/generalRealEstate/inputs'
import {
    generalRealEstateFlatLeaseValidaton,
    generalRealEstateFlatSaleValidaton,
    generalRealEstateValidaton,
    propertyGeneralGarageLeaseValidaton,
    propertyGeneralGarageSaleValidaton,
    propertyGeneralHouseLeaseValidaton,
    propertyGeneralHouseSaleValidaton,
    propertyGeneralLandLeaseValidaton,
    propertyGeneralLandSaleValidaton,
    propertyGeneralOfficeSpaceLeaseValidaton,
    propertyGeneralOfficeSpaceSaleValidaton,
} from '../../../common/forms/generalRealEstate/validations'
import { selectPropertyServiceFilter, selectPropertyTypeFilter } from '../../../store/formFilters'
import { addRequiredValidation } from '../../../utils/formUtils/forms'

// TODO: type validation functions
interface ValidationConfig<T extends ObjectShape> {
    initialValues: T
    validationSchema: Yup.ObjectSchema<T>
    prepareModelForSubmit: (values: T) => Partial<PropertyCreatePayload>
}

/**
 * @param isEdit - true for edit mode, false for create mode
 * @param data - Full Property object (edit mode) or LocationSource with agent's default location (create mode)
 */
export const usePropertyGeneralFormConfig = (isEdit: boolean, data: Property | LocationSource | null) => {
    const { data: currentAgentId } = auth.CurrentAgent.useQuery({ select: (data) => data.id })

    const { storePropertyTypeId, storePropertyServiceId } = useSelector((state) => ({
        storePropertyTypeId: selectPropertyTypeFilter(state),
        storePropertyServiceId: selectPropertyServiceFilter(state),
    }))

    // Check if data is a full Property object (vs LocationSource with just location fields)
    const isPropertyData = data && 'propertyType' in data && 'propertyService' in data

    const propertyTypeId = isPropertyData ? (data as Property).propertyType?.id : storePropertyTypeId
    const propertyServiceId = isPropertyData ? (data as Property).propertyService?.id : storePropertyServiceId

    const appSettings = useMemo(
        () => ({
            currency: APPLICATION_SETTINGS.CURRENCY,
            lang: APPLICATION_SETTINGS.DEFAULT_LANGUAGE,
            areaUnit: APPLICATION_SETTINGS.AREA_UNIT,
        }),
        []
    )

    const validationData = useMemo(() => {
        // edit/clone mode
        if (isPropertyData) return data

        // create mode
        return {
            propertyService: { id: propertyServiceId ? +propertyServiceId : '' },
            propertyType: { id: propertyTypeId ? +propertyTypeId : '' },
            ...data, // agent location data (country, county, city)
        }
    }, [data, isPropertyData, propertyServiceId, propertyTypeId])

    const formConfig = useMemo(() => {
        const isServiceAndTypeSelected = propertyServiceId && propertyTypeId
        const type = isServiceAndTypeSelected ? +propertyTypeId : null
        const isSale = propertyServiceId?.toString() === REAL_ESTATE_SERVICE.sale?.toString()
        const isLease = propertyServiceId?.toString() === REAL_ESTATE_SERVICE.lease?.toString()

        let formInputs = null
        let formikConfig: ValidationConfig<any> | null = null

        switch (type) {
            case REAL_ESTATE_TYPES.house:
                if (isSale) {
                    formInputs = realEstateGeneralHouseSaleInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralHouseSaleValidaton(appSettings, validationData)
                } else if (isLease) {
                    formInputs = realEstateGeneralHouseLeaseInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralHouseLeaseValidaton(appSettings, validationData)
                }
                break
            case REAL_ESTATE_TYPES.flat:
                if (isSale) {
                    formInputs = realEstateGeneralFlatSaleInputs(isEdit, appSettings)
                    formikConfig = generalRealEstateFlatSaleValidaton(appSettings, validationData)
                } else if (isLease) {
                    formInputs = realEstateGeneralFlatLeaseInputs(isEdit, appSettings)
                    formikConfig = generalRealEstateFlatLeaseValidaton(appSettings, validationData)
                }
                break
            case REAL_ESTATE_TYPES.officeSpace:
                if (isSale) {
                    formInputs = realEstateGeneralOfficeSpaceSaleInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralOfficeSpaceSaleValidaton(appSettings, validationData)
                } else if (isLease) {
                    formInputs = realEstateGeneralOfficeSpaceLeaseInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralOfficeSpaceLeaseValidaton(appSettings, validationData)
                }
                break
            case REAL_ESTATE_TYPES.land:
                if (isSale) {
                    formInputs = realEstateGeneralLandSaleInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralLandSaleValidaton(appSettings, validationData)
                } else if (isLease) {
                    formInputs = realEstateGeneralLandLeaseInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralLandLeaseValidaton(appSettings, validationData)
                }
                break
            case REAL_ESTATE_TYPES.garage:
                if (isSale) {
                    formInputs = realEstateGeneralGarageSaleInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralGarageSaleValidaton(appSettings, validationData)
                } else if (isLease) {
                    formInputs = realEstateGeneralGarageLeaseInputs(isEdit, appSettings)
                    formikConfig = propertyGeneralGarageLeaseValidaton(appSettings, validationData)
                }
                break
            default:
                formInputs = realEstateGeneralInputs(isEdit, appSettings)
                formikConfig = generalRealEstateValidaton(appSettings)
                break
        }

        if (formikConfig && !formikConfig.initialValues[realEstateGeneralFields.PRIMARY_AGENT_ID] && currentAgentId) {
            formikConfig.initialValues[realEstateGeneralFields.PRIMARY_AGENT_ID] = currentAgentId
        }

        if (formInputs && formikConfig) {
            const flattenedFormInputs = _.flattenDeep([formInputs.sideForm, formInputs.mainForm])
            addRequiredValidation(formikConfig.validationSchema.fields, flattenedFormInputs)
        }

        return {
            formInputs,
            formikConfig,
        }
    }, [appSettings, currentAgentId, isEdit, propertyServiceId, propertyTypeId, validationData])

    return formConfig
}
